import { createMemo, Show } from 'solid-js';
import { Badge, type BadgeTone } from './Badge';
import Text from './Text';
import SearchableItemPicker, { Highlight, type PickerEmptyOption } from './SearchableItemPicker';
import { useTranslation } from '../hooks/useTranslation';
import { formatLocation, parseLocation } from '../services/gameLocation';
import type { StorageInfo } from '../services/inventoryApi';
import styles from './StorageSelect.module.css';

const CITYBUILD_TONES: Record<string, BadgeTone> = {
    Nature: 'success',
    Wasser: 'accent',
    Extreme: 'warning',
    Lava: 'warning',
    Evil: 'error',
};

const citybuildTone = (citybuildName: string): BadgeTone => CITYBUILD_TONES[citybuildName] ?? 'info';

const locationCommand = (storage: StorageInfo) => {
    const location = parseLocation(storage.location);
    return location ? formatLocation(location) : '';
};

const scanPercent = (storage: StorageInfo) =>
    storage.chestCount > 0 ? Math.round(storage.scannedChestCount / storage.chestCount * 100) : 0;

const storageIcon = (storage: StorageInfo) => storage.isOwner ? 'fa-box' : 'fa-share-nodes';

function Route(props: { storage: StorageInfo; query: string }) {
    const command = () => locationCommand(props.storage);
    return (
        <span class={styles.route}>
            <Badge tone={citybuildTone(props.storage.citybuildName)}>
                <Highlight text={props.storage.citybuildName} query={props.query} />
            </Badge>
            <i class={`fa-solid fa-chevron-right ${styles.routeArrow}`} aria-hidden="true" />
            <Show when={command()} fallback={<span class={`${styles.command} ${styles.commandMissing}`}><Text val="LOCATION_NOT_SET" /></span>}>
                <span class={styles.command} title={command()}><Highlight text={command()} query={props.query} /></span>
            </Show>
        </span>
    );
}

export default function StorageSelect(props: {
    storages: StorageInfo[];
    value: string;
    onChange: (storageId: string) => void;
    placeholder?: string;
    emptyOption?: PickerEmptyOption;
    disabled?: boolean;
}) {
    const { t } = useTranslation();

    const storagesOwnedFirst = createMemo(() => [
        ...props.storages.filter(storage => storage.isOwner),
        ...props.storages.filter(storage => !storage.isOwner),
    ]);

    return (
        <SearchableItemPicker
            choices={storagesOwnedFirst()}
            keyOf={storage => storage.id}
            searchTextOf={storage => [storage.name, storage.citybuildName, storage.ownerName, locationCommand(storage)].join(' ')}
            sectionTitleKeyOf={storage => storage.isOwner ? 'STORAGE_SECTION_OWNED' : 'STORAGE_SECTION_SHARED'}
            value={props.value}
            onChange={props.onChange}
            placeholder={props.storages.length === 0 ? t('STORAGE_SELECT_EMPTY') : (props.placeholder ?? t('STORAGE_SELECT_PLACEHOLDER'))}
            searchPlaceholder={t('STORAGE_SEARCH_STORAGES')}
            noMatchKey="STORAGE_NO_MATCH"
            emptyOption={props.emptyOption}
            disabled={props.disabled}
            renderSelected={storage => (
                <>
                    <i class={`fa-solid ${storageIcon(storage)} ${styles.triggerIcon}`} aria-hidden="true" />
                    <span class={styles.body}>
                        <span class={styles.name}>{storage.name}</span>
                        <Route storage={storage} query="" />
                    </span>
                </>
            )}
            renderOption={(storage, query) => (
                <>
                    <span class={styles.lead}>
                        <i class={`fa-solid ${storageIcon(storage)}`} aria-hidden="true" />
                    </span>
                    <span class={styles.body}>
                        <span class={styles.lineTop}>
                            <span class={styles.name}><Highlight text={storage.name} query={query()} /></span>
                            <span class={styles.stats} title={t('STORAGE_SELECT_SCANNED', storage.scannedChestCount, storage.chestCount)}>
                                <i class="fa-solid fa-box-archive" aria-hidden="true" />
                                {storage.chestCount}
                                <span class={styles.scanBar}><span style={{ width: `${scanPercent(storage)}%` }} /></span>
                                {scanPercent(storage)}%
                            </span>
                        </span>
                        <span class={styles.lineBottom}>
                            <Route storage={storage} query={query()} />
                            <Show when={!storage.isOwner}>
                                <span class={styles.owner}>
                                    <i class="fa-solid fa-user" aria-hidden="true" />
                                    <Highlight text={storage.ownerName} query={query()} />
                                </span>
                            </Show>
                        </span>
                    </span>
                </>
            )}
        />
    );
}
